import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, Loader2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import { API, getAuthHeaders } from '../../utils/adminAuth';

export default function TeamMemberModal({ member, onClose, onSave }) {
  const [form, setForm] = useState({
    name: member?.name || '',
    role: member?.role || 'Core Team',
    bio: member?.bio || '',
    image_url: member?.image_url || '',
    is_active: member?.is_active ?? true,
  });
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/team-image`, fd);
      const url = `${window.location.origin}/api/files/${res.data.storage_path}`;
      setForm(f => ({ ...f, image_url: url }));
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (member) {
        await axios.put(`${API}/admin/team/${member.id}`, form, { headers: getAuthHeaders() });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/admin/team`, form, { headers: getAuthHeaders() });
        toast.success('Member added');
      }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#1a1a1a] border border-white/10 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="team-modal">
        <div className="p-6 border-b border-white/5 flex items-center justify-between">
          <h3 className="text-white text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {member ? 'Edit Member' : 'Add Team Member'}
          </h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-center">
            {form.image_url ? (
              <div className="relative inline-block">
                <img src={form.image_url} alt="" className="w-24 h-24 rounded-full object-cover mx-auto border-2 border-[#1C7D3F]/30" />
                <button onClick={() => setForm(f => ({ ...f, image_url: '' }))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <label className="w-24 h-24 rounded-full bg-white/5 border border-dashed border-white/20 flex items-center justify-center mx-auto cursor-pointer hover:border-[#1C7D3F] transition-colors">
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                {uploading ? <Loader2 className="w-5 h-5 animate-spin text-white/40" /> : <Upload className="w-5 h-5 text-white/30" />}
              </label>
            )}
            <p className="text-white/30 text-xs mt-2 font-sans">Profile photo</p>
          </div>
          <div>
            <label className="text-white/60 text-xs font-sans block mb-1">Name *</label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#1C7D3F] focus:outline-none" data-testid="team-name-input" />
          </div>
          <div>
            <label className="text-white/60 text-xs font-sans block mb-1">Role</label>
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              className="w-full bg-[#1a1a1a] border border-white/20 text-white text-sm px-3 py-2 focus:border-[#1C7D3F] focus:outline-none" data-testid="team-role-select">
              <option value="Founder">Founder</option>
              <option value="Co-Founder">Co-Founder</option>
              <option value="Ambassador">Ambassador</option>
              <option value="Core Team">Core Team</option>
              <option value="Brand Partner">Brand Partner</option>
            </select>
          </div>
          <div>
            <label className="text-white/60 text-xs font-sans block mb-1">Short Bio</label>
            <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} rows={3}
              className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#1C7D3F] focus:outline-none resize-none" data-testid="team-bio-input" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-xs font-sans">Visible on website</span>
            <Switch checked={form.is_active} onCheckedChange={val => setForm(f => ({ ...f, is_active: val }))} data-testid="team-active-toggle" />
          </div>
        </div>
        <div className="p-6 border-t border-white/5 flex gap-3">
          <button onClick={onClose} className="flex-1 border border-white/20 text-white/60 py-2 text-sm hover:border-white/40 transition-colors">Cancel</button>
          <button onClick={handleSave} className="flex-1 bg-[#1C7D3F] text-white py-2 text-sm hover:bg-[#2E9B50] transition-colors" data-testid="team-save-btn">
            {member ? 'Update' : 'Add Member'}
          </button>
        </div>
      </div>
    </div>
  );
}
